import { jsPDF } from 'jspdf'

import type { ProposalDocument } from '@/modules/proposal'

import type { DeliveryBranding, PdfExportResult } from '../types'
import type { ClientPdfDocument } from './client-pdf-document'
import { buildPdfFilename } from './client-pdf-document'
import { proposalToClientPdf } from './proposal-to-client-pdf'

const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const GREEN = { r: 22, g: 163, b: 74 }
const DARK = { r: 5, g: 46, b: 22 }
const INK = { r: 23, g: 23, b: 23 }
const MUTED = { r: 82, g: 82, b: 82 }
const LINE = { r: 220, g: 220, b: 220 }

/**
 * A4 commercial PDF — exclusive input: ProposalDocument or ClientPdfDocument.
 * Does not print internal financial fields.
 */
export class ProposalPdfGenerator {
  generate(proposal: ProposalDocument, branding: DeliveryBranding): PdfExportResult {
    const client = proposalToClientPdf(proposal, branding)
    const generated = this.generateFromClient(client)
    return { ...generated, proposal }
  }

  generateFromClient(document: ClientPdfDocument): Omit<PdfExportResult, 'proposal'> & {
    proposal?: ProposalDocument
  } {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' })
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()
    const margin = 48
    const contentWidth = pageWidth - margin * 2
    let y = margin

    doc.setFillColor(GREEN.r, GREEN.g, GREEN.b)
    doc.rect(0, 0, pageWidth, 6, 'F')

    drawLogoMark(doc, margin, 22)

    doc.setTextColor(DARK.r, DARK.g, DARK.b)
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(16)
    doc.text(document.company.name.toUpperCase(), margin + 36, 36)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(document.company.tagline.toUpperCase(), margin + 36, 48)

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(10)
    doc.setTextColor(DARK.r, DARK.g, DARK.b)
    doc.text(document.number, pageWidth - margin, 36, { align: 'right' })
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
    doc.text(document.issuedAtLabel, pageWidth - margin, 48, { align: 'right' })

    if (document.company.logoDataUrl) {
      try {
        doc.addImage(document.company.logoDataUrl, 'PNG', pageWidth - margin - 120, 18, 28, 28)
      } catch {
        // Keep the vector mark when the configured logo is not a valid raster.
      }
    }

    y = 72
    const inst = [
      document.company.cnpj ? `CNPJ ${document.company.cnpj}` : '',
      document.company.phone ?? '',
      document.company.email ?? '',
      document.company.address ?? '',
    ].filter(Boolean)
    if (inst.length > 0) {
      doc.setFontSize(8)
      y = writeLine(doc, inst.join('  ·  '), margin, y, contentWidth)
      y += 6
    }

    doc.setDrawColor(GREEN.r, GREEN.g, GREEN.b)
    doc.setLineWidth(1)
    doc.line(margin, y, pageWidth - margin, y)
    y += 18

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(14)
    doc.setTextColor(DARK.r, DARK.g, DARK.b)
    y = writeLine(doc, document.title, margin, y, contentWidth)
    y += 8

    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(INK.r, INK.g, INK.b)
    y = writeLine(doc, `Cliente: ${document.client.name}`, margin, y, contentWidth)
    if (document.client.document) {
      y = writeLine(doc, `Documento: ${document.client.document}`, margin, y, contentWidth)
    }
    if (document.client.email) {
      y = writeLine(doc, `E-mail: ${document.client.email}`, margin, y, contentWidth)
    }
    if (document.client.phone) {
      y = writeLine(doc, `Telefone: ${document.client.phone}`, margin, y, contentWidth)
    }
    const meta: string[] = []
    if (document.statusLabel) meta.push(`Status: ${document.statusLabel}`)
    if (document.terms.validity) meta.push(`Validade: ${document.terms.validity}`)
    if (meta.length) {
      y = writeLine(doc, meta.join('  ·  '), margin, y, contentWidth)
    }
    y += 14

    y = drawTableHeader(doc, margin, pageWidth, y)
    for (const [index, item] of document.items.entries()) {
      y = drawItemRow(doc, item, index + 1, margin, pageWidth, y)
    }

    y += 10
    y = ensureSpace(doc, y, margin, 90)
    doc.setDrawColor(LINE.r, LINE.g, LINE.b)
    doc.line(margin, y, pageWidth - margin, y)
    y += 16

    doc.setFont('helvetica', 'bold')
    doc.setFontSize(11)
    doc.setTextColor(DARK.r, DARK.g, DARK.b)
    doc.text('Resumo', margin, y)
    y += 16
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    doc.setTextColor(INK.r, INK.g, INK.b)
    y = moneyRow(doc, 'Subtotal', document.summary.subtotal, margin, pageWidth, y)
    if (document.summary.discountTotal > 0) {
      y = moneyRow(doc, 'Desconto', document.summary.discountTotal, margin, pageWidth, y)
    }
    if (document.summary.shipping > 0) {
      y = moneyRow(doc, 'Frete', document.summary.shipping, margin, pageWidth, y)
    }
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(12)
    doc.setTextColor(DARK.r, DARK.g, DARK.b)
    y = moneyRow(doc, 'Valor final', document.summary.total, margin, pageWidth, y)
    y += 12

    const termLines = [
      document.terms.payment ? `Pagamento: ${document.terms.payment}` : '',
      document.terms.validity ? `Validade: ${document.terms.validity}` : '',
      document.terms.warranty ? `Garantia: ${document.terms.warranty}` : '',
      document.terms.delivery ? `Prazo / entrega: ${document.terms.delivery}` : '',
    ].filter(Boolean)
    if (termLines.length > 0) {
      y = ensureSpace(doc, y, margin, 70)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.text('Condições', margin, y)
      y += 14
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
      for (const line of termLines) {
        y = writeLine(doc, line, margin, y, contentWidth)
      }
      y += 8
    }

    if (document.notes) {
      y = ensureSpace(doc, y, margin, 50)
      doc.setFont('helvetica', 'bold')
      doc.setFontSize(11)
      doc.setTextColor(DARK.r, DARK.g, DARK.b)
      doc.text('Observações', margin, y)
      y += 14
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(9)
      doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
      writeLine(doc, document.notes, margin, y, contentWidth)
    }

    const pageCount = doc.getNumberOfPages()
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i)
      doc.setFillColor(248, 250, 248)
      doc.rect(0, pageHeight - 36, pageWidth, 36, 'F')
      doc.setFontSize(8)
      doc.setTextColor(MUTED.r, MUTED.g, MUTED.b)
      doc.text(
        'Documento gerado pelo MasterHub',
        margin,
        pageHeight - 18,
      )
      doc.text(
        `Página ${i} / ${pageCount}`,
        pageWidth - margin,
        pageHeight - 18,
        { align: 'right' },
      )
    }

    const bytes = new Uint8Array(doc.output('arraybuffer'))
    const blob = new Blob([bytes], { type: 'application/pdf' })
    const filename = buildPdfFilename(document.number)

    return { ok: true, filename, blob, bytes }
  }

  download(proposal: ProposalDocument, branding: DeliveryBranding): PdfExportResult {
    const result = this.generate(proposal, branding)
    triggerPdfDownload(result.blob, result.filename)
    return result
  }

  downloadClient(document: ClientPdfDocument) {
    const result = this.generateFromClient(document)
    triggerPdfDownload(result.blob, result.filename)
    return result
  }
}

export function triggerPdfDownload(blob: Blob, filename: string): void {
  if (typeof document === 'undefined') return
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 1_000)
}

function drawLogoMark(doc: jsPDF, x: number, y: number): void {
  doc.setFillColor(GREEN.r, GREEN.g, GREEN.b)
  doc.roundedRect(x, y, 28, 28, 6, 6, 'F')
  doc.setDrawColor(DARK.r, DARK.g, DARK.b)
  doc.setLineWidth(2)
  doc.line(x + 7, y + 21, x + 7, y + 8)
  doc.line(x + 7, y + 8, x + 14, y + 16)
  doc.line(x + 14, y + 16, x + 21, y + 8)
  doc.line(x + 21, y + 8, x + 21, y + 21)
}

function drawTableHeader(doc: jsPDF, margin: number, pageWidth: number, y: number): number {
  y = ensureSpace(doc, y, margin, 24)
  doc.setFillColor(240, 253, 244)
  doc.rect(margin, y - 12, pageWidth - margin * 2, 20, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(8)
  doc.setTextColor(DARK.r, DARK.g, DARK.b)
  doc.text('#', margin + 6, y)
  doc.text('Descrição', margin + 28, y)
  doc.text('Qtd', pageWidth - margin - 150, y, { align: 'right' })
  doc.text('Unitário', pageWidth - margin - 78, y, { align: 'right' })
  doc.text('Total', pageWidth - margin - 4, y, { align: 'right' })
  return y + 16
}

function drawItemRow(
  doc: jsPDF,
  item: ClientPdfDocument['items'][number],
  position: number,
  margin: number,
  pageWidth: number,
  y: number,
): number {
  const descWidth = pageWidth - margin * 2 - 170
  const lines = doc.splitTextToSize(item.description, descWidth) as string[]
  const rowHeight = Math.max(16, lines.length * 11 + 8)
  y = ensureSpace(doc, y, margin, rowHeight + 4)

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(8)
  doc.setTextColor(INK.r, INK.g, INK.b)
  doc.text(String(position), margin + 6, y)
  doc.text(lines, margin + 28, y)
  doc.text(String(item.quantity), pageWidth - margin - 150, y, { align: 'right' })
  doc.text(money.format(item.unitPrice), pageWidth - margin - 78, y, { align: 'right' })
  doc.setFont('helvetica', 'bold')
  doc.text(money.format(item.lineTotal), pageWidth - margin - 4, y, { align: 'right' })
  return y + rowHeight
}

function writeLine(doc: jsPDF, text: string, x: number, y: number, maxWidth: number): number {
  const lines = doc.splitTextToSize(text, maxWidth) as string[]
  doc.text(lines, x, y)
  return y + lines.length * 12
}

function moneyRow(
  doc: jsPDF,
  label: string,
  value: number,
  margin: number,
  pageWidth: number,
  y: number,
): number {
  doc.text(label, margin, y)
  doc.text(money.format(value), pageWidth - margin, y, { align: 'right' })
  return y + 14
}

function ensureSpace(doc: jsPDF, y: number, margin: number, needed: number): number {
  const max = doc.internal.pageSize.getHeight() - margin - 40
  if (y + needed <= max) return y
  doc.addPage()
  return margin
}

export const proposalPdfGenerator = new ProposalPdfGenerator()
